import { getLocalCache, setLocalCache, accessTokenKey } from 'stores/localStorage'
import jwt from 'libs/crypt/jwt'
import { AUTH_LOGIN, AUTH_VERIFY_REGISTER, AUTH_LOGOUT, AUTH_RESET_PASSWORD } from 'api/wenjetso/graphql/mutations'
// eslint-disable-next-line import/no-cycle
import { userStoreId, companyStoreId } from 'stores'

/**
 * @param token
 */
function generateTokenObject(token) {
  const payload = jwt.getPayload(token)
  return {
    token: jwt.isExpired(payload) ? null : token,
    payload
  }
}

/**
 * Auth Store
 * 
 * @typedef {object} AuthStore
 * @property {AuthState} state - Auth state
 * @property {AuthAction} actions - Auth actions
 * */


/**
 * Auth State
 * 
 * @typedef {object} AuthState
 * @property {string} token - Acess Token
 * @property {object} payload - Jwt payload
 */
const initState = {
  ...generateTokenObject(getLocalCache(accessTokenKey)),
  isInitial: false,
}

const actionTypes = {
  setToken: 'setToken',
  setInitial: 'setInitial',
  login: 'login'
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.setToken: {
      setLocalCache(accessTokenKey, action.token)
      return {
        ...state,
        ...generateTokenObject(action.token)
      }
    }
    case actionTypes.setInitial: {
      return {
        ...state,
        isInitial: action.isInitial
      }
    }
    default:
      return state
  }
}

/**
 * Auth Getters
 * 
 * @typedef {object} AuthGetter
 * @property {Function} isLoggedIn - Check whether login
 */

/** @type {import('libs/store').StoreGettersInterface} */
// eslint-disable-next-line func-names
const getters = function ({state}) {
  this.isLoggedIn = () => state.token && !jwt.isExpired(state.payload) && !this.isAnonymous()
  this.isAnonymous = () => state.payload ? state.payload.is_anonymous : true
  this.isInitial = () => state.isInitial
  this.getAccessToken = () => state.token
  this.isAccessTokenExpired = () => jwt.isExpired(state.payload)
  return this
}

/**
 * Auth Actions
 * 
 * @typedef {object} AuthAction
 * @property {Function} setAccessToken - Set access token
 */

 /** @type {import('libs/store').StoreActionsInterface} */
// eslint-disable-next-line func-names
const actions = function ({ dispatch, state, plugins, useStore }) {
  const { requestGraphQL } = plugins

  this.handleInitApp = async () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const userStore = useStore(userStoreId)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const companyStore = useStore(companyStoreId)
    const responses = await Promise.all([
      userStore.actions.fetchUser(),
      companyStore.actions.fetchCompany(),
    ])
    const hasError = responses.some(response => !!response.error)
    if (!hasError) {
      this.setInitialFinish()
    }
  }

  this.handleClearApp = async () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const userStore = useStore(userStoreId)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const companyStore = useStore(companyStoreId)
    this.setAccessToken(null)
    userStore.actions.setUserProfile(null)
    companyStore.actions.clear()
  }

  this.setAccessToken = (token) => {
    dispatch({type : actionTypes.setToken, token})
  }

  this.setInitialFinish = () => {
    dispatch({type : actionTypes.setInitial, isInitial: true})
  }

  this.login = async ({userName, password, loginMethod}) => {
    const response = await requestGraphQL(AUTH_LOGIN, {
      input: {
        username: userName,
        password,
        login_method: loginMethod
      }
    })
    if (!response.error) {
      const payload = response.auth_login
      this.setAccessToken(payload.access_token)
      await this.handleInitApp()
    }
    return response
  }
  this.verifyEmail = async(token) => {
    const response = await requestGraphQL(AUTH_VERIFY_REGISTER, {
      token
    })
    if (!response.error) {
      const payload = response.auth_verify_register
      this.setAccessToken(payload.access_token)
      await this.handleInitApp()
    }
    return response
  }
  this.logout = async () => {
    const response = await requestGraphQL(AUTH_LOGOUT, {}, state.token)
    if (!response.error) {
      this.handleClearApp()
    }
    return response
  }
  this.resetPassword = async ({token, password, lang}) => {
    const response = await requestGraphQL(AUTH_RESET_PASSWORD, {
      input: {
        token,
        password
      }
    }, null, {
      lang
    })
    if (!response.error) {
      const payload = response.auth_reset_password
      this.setAccessToken(payload.access_token)
      await this.handleInitApp()
    }
    return response
  }

  return this
}

 /**
  * @type {import('libs/store').StoreInterface}
  */
export const authStore = {
  initState,
  reducer,
  getters,
  actions,
}